/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getBytesDecoder,
  getBytesEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
  type WritableSignerAccount,
} from "@solana/kit";
import { GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS } from "../programs";
import { getAccountMetaFactory, type ResolvedAccount } from "../shared";

export const RESET_REENTRANCY_GUARD_DISCRIMINATOR = new Uint8Array([
  93, 74, 168, 125, 46, 153, 9, 255,
]);

export function getResetReentrancyGuardDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    RESET_REENTRANCY_GUARD_DISCRIMINATOR,
  );
}

export type ResetReentrancyGuardInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountReentrancyGuard extends string | AccountMeta<string> = string,
  TAccountAuthority extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountReentrancyGuard extends string
        ? WritableAccount<TAccountReentrancyGuard>
        : TAccountReentrancyGuard,
      TAccountAuthority extends string
        ? WritableSignerAccount<TAccountAuthority> &
            AccountSignerMeta<TAccountAuthority>
        : TAccountAuthority,
      ...TRemainingAccounts,
    ]
  >;

export type ResetReentrancyGuardInstructionData = {
  discriminator: ReadonlyUint8Array;
};

export type ResetReentrancyGuardInstructionDataArgs = {};

export function getResetReentrancyGuardInstructionDataEncoder(): FixedSizeEncoder<ResetReentrancyGuardInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([["discriminator", fixEncoderSize(getBytesEncoder(), 8)]]),
    (value) => ({
      ...value,
      discriminator: RESET_REENTRANCY_GUARD_DISCRIMINATOR,
    }),
  );
}

export function getResetReentrancyGuardInstructionDataDecoder(): FixedSizeDecoder<ResetReentrancyGuardInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
  ]);
}

export function getResetReentrancyGuardInstructionDataCodec(): FixedSizeCodec<
  ResetReentrancyGuardInstructionDataArgs,
  ResetReentrancyGuardInstructionData
> {
  return combineCodec(
    getResetReentrancyGuardInstructionDataEncoder(),
    getResetReentrancyGuardInstructionDataDecoder(),
  );
}

export type ResetReentrancyGuardAsyncInput<
  TAccountReentrancyGuard extends string = string,
  TAccountAuthority extends string = string,
> = {
  reentrancyGuard?: Address<TAccountReentrancyGuard>;
  authority: TransactionSigner<TAccountAuthority>;
};

export async function getResetReentrancyGuardInstructionAsync<
  TAccountReentrancyGuard extends string,
  TAccountAuthority extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: ResetReentrancyGuardAsyncInput<
    TAccountReentrancyGuard,
    TAccountAuthority
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  ResetReentrancyGuardInstruction<
    TProgramAddress,
    TAccountReentrancyGuard,
    TAccountAuthority
  >
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    reentrancyGuard: { value: input.reentrancyGuard ?? null, isWritable: true },
    authority: { value: input.authority ?? null, isWritable: true },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Resolve default values.
  if (!accounts.reentrancyGuard.value) {
    accounts.reentrancyGuard.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([
            114, 101, 101, 110, 116, 114, 97, 110, 99, 121, 95, 103, 117, 97,
            114, 100,
          ]),
        ),
      ],
    });
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.reentrancyGuard),
      getAccountMeta(accounts.authority),
    ],
    data: getResetReentrancyGuardInstructionDataEncoder().encode({}),
    programAddress,
  } as ResetReentrancyGuardInstruction<
    TProgramAddress,
    TAccountReentrancyGuard,
    TAccountAuthority
  >);
}

export type ResetReentrancyGuardInput<
  TAccountReentrancyGuard extends string = string,
  TAccountAuthority extends string = string,
> = {
  reentrancyGuard: Address<TAccountReentrancyGuard>;
  authority: TransactionSigner<TAccountAuthority>;
};

export function getResetReentrancyGuardInstruction<
  TAccountReentrancyGuard extends string,
  TAccountAuthority extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: ResetReentrancyGuardInput<TAccountReentrancyGuard, TAccountAuthority>,
  config?: { programAddress?: TProgramAddress },
): ResetReentrancyGuardInstruction<
  TProgramAddress,
  TAccountReentrancyGuard,
  TAccountAuthority
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    reentrancyGuard: { value: input.reentrancyGuard ?? null, isWritable: true },
    authority: { value: input.authority ?? null, isWritable: true },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.reentrancyGuard),
      getAccountMeta(accounts.authority),
    ],
    data: getResetReentrancyGuardInstructionDataEncoder().encode({}),
    programAddress,
  } as ResetReentrancyGuardInstruction<
    TProgramAddress,
    TAccountReentrancyGuard,
    TAccountAuthority
  >);
}

export type ParsedResetReentrancyGuardInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    reentrancyGuard: TAccountMetas[0];
    authority: TAccountMetas[1];
  };
  data: ResetReentrancyGuardInstructionData;
};

export function parseResetReentrancyGuardInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedResetReentrancyGuardInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 2) {
    // TODO: Coded error.
    throw new Error("Not enough accounts");
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      reentrancyGuard: getNextAccount(),
      authority: getNextAccount(),
    },
    data: getResetReentrancyGuardInstructionDataDecoder().decode(
      instruction.data,
    ),
  };
}
